// src/lib/purchasingKpis.js
// #308 calculation layer, #302's calculation half. Session 74z6.
//
// The executive dashboard's math, settled and proven before the page that renders
// it exists. Colby's requirement is that this layer be correct at both the app and
// database level before deployment day; the page lands at the Sep 5-6 sprint.
//
// THREE CONTRACTS, and they are the reason this file is shaped the way it is.
//
// 1. PURE FUNCTIONS OVER ROW ARRAYS. Nothing here fetches. A KPI function never
//    learns where its rows came from, which is what makes the eventual Postgres
//    port transcription rather than redesign. When aggregation moves server-side
//    — a year of history, scheduled email delivery, or JWT/RLS at Sep 22-26 —
//    the definitions move without being rewritten.
//
// 2. EVERY FUNCTION TAKES AN OPTIONAL BRANCH ARGUMENT, in the same position,
//    and nothing passes one in v1. #311's branch-scoped view cannot retrofit a
//    function that cannot accept one. PURCHASING KPIs ACCEPT IT AND DISCARD IT
//    DELIBERATELY — purchasing is run from Houston for requests originating at
//    Dallas, so scoping a purchasing figure to a branch produces a number that
//    means nothing. The discard is written at the discard, not in this header.
//
// 3. HONEST EMPTY RESULTS. Never 0, never NaN, never a divide-by-zero. Dallas
//    starts Sep 1 with no history and the first thing this math is asked to do
//    is describe nothing. A ratio with an empty denominator is not zero percent.
//    Empty results carry a REASON and a NEEDS count so the tile can say what is
//    missing and how much would be enough.
//
// NO FORMATTING HERE. Functions return numbers; the page decides that 48210
// looks like $48,210. A KPI that returns a formatted string cannot be charted,
// summed or exported.
//
// AGING IS DELIBERATELY ABSENT — see the note at the foot of this file.

import { firstPassRate } from './invoiceDiscrepancy';
import { isCredit, isInvoiceDoc, netInvoiced } from './vendorCredit';
import { needsAttention } from './stockStatus';

// ── The fetch this module's functions expect ─────────────────────────────────

// Exported so the dashboard's query cannot drift out of agreement with the math.
// Note two columns InvoicingPage.js does NOT select and these functions need:
// material_requests.received_at and material_request_items.needed_by.
export const KPI_REQUEST_SELECT = `
  id, request_number, branch_id, status, created_at, ordered_at, received_at,
  vendor_id, ordered_price,
  inventory_vendors ( id, name ),
  material_request_items (
    id, item_id, qty_requested, qty_received, needed_by,
    is_off_catalog, ordered_price,
    receipt_closed, receipt_close_reason,
    material_request_invoice_lines ( id, invoice_id, qty_invoiced, line_amount )
  ),
  material_request_invoices (
    id, invoice_date, due_date, document_type, credit_of_invoice_id,
    total_amount, discrepancy_flagged, discrepancy_amount,
    discrepancy_resolved, discrepancy_resolution
  )
`;

// ── Empty-result vocabulary ──────────────────────────────────────────────────

// Spelled once so a tile's copy is generated from the calculation rather than
// written into the component and left to drift.
export const EMPTY = {
  NO_ORDERS:     'no_orders',      // nothing has ever been ordered
  NO_OPEN:       'no_open_orders', // ordered, but nothing outstanding
  NO_INVOICES:   'no_invoices',    // none in the selected period
  NO_RECEIPTS:   'no_receipts',
  NO_LINES:      'no_ordered_lines',
  NO_NEED_BY:    'no_need_by_dates',
  NO_STOCK:      'no_stock_rows',
  NOTHING_FLAGGED: 'nothing_flagged', // the one case where empty is good news
  ALL_INVOICED:    'all_invoiced',    // likewise
};

function empty(reason, needs = 1) {
  return { value: null, empty: reason, needs };
}

function ok(value, meta = {}) {
  return { value, empty: null, needs: 0, ...meta };
}

export function isEmpty(kpi) {
  return !kpi || kpi.value == null;
}

function num(v) {
  if (v == null) return 0;
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

function days(fromIso, toIso) {
  if (!fromIso || !toIso) return null;
  const a = new Date(fromIso).getTime();
  const b = new Date(toIso).getTime();
  if (isNaN(a) || isNaN(b)) return null;
  return (b - a) / 86400000;
}

function mean(list) {
  if (!list.length) return null;
  return list.reduce((s, n) => s + n, 0) / list.length;
}

// ── Row helpers ──────────────────────────────────────────────────────────────

function itemsOf(request) {
  return request?.material_request_items || [];
}

function docsOf(request) {
  return request?.material_request_invoices || [];
}

// What one order line has been billed for, across every invoice that touched it.
function invoicedForLine(item) {
  return (item?.material_request_invoice_lines || [])
    .reduce((sum, l) => sum + num(l.line_amount), 0);
}

// The open commitment on one line: ordered value not yet billed, floored at zero.
// Floored rather than allowed negative because an over-billed line is a
// discrepancy, and it is reported as one — letting it subtract here would
// silently net a billing error against an unrelated order's commitment.
function openCommitmentForLine(item) {
  return Math.max(0, num(item?.ordered_price) - invoicedForLine(item));
}

// ── Branch scoping ───────────────────────────────────────────────────────────

// Applied by the INVENTORY-side KPIs only. Purchasing KPIs call nothing here and
// say why at the point they don't.
function scopeToBranch(requests, branchId) {
  if (branchId == null) return requests;
  return requests.filter(r => String(r.branch_id) === String(branchId));
}

// ── Period filtering ─────────────────────────────────────────────────────────

// Invoice documents are filtered on invoice_date — the date the vendor put on the
// document — not entered_at, which is when somebody typed it. An invoice entered
// late still belongs to the month it was issued in.
function inPeriod(dateStr, from, to) {
  if (!from && !to) return true;
  if (!dateStr) return false;
  const d = String(dateStr).slice(0, 10);
  if (from && d < String(from).slice(0, 10)) return false;
  if (to && d > String(to).slice(0, 10)) return false;
  return true;
}

function docsInPeriod(requests, from, to) {
  return requests.flatMap(r => docsOf(r).filter(d => inPeriod(d.invoice_date, from, to)));
}

// ═══════════════════════════════════════════════════════════════════════════
// MONEY
// ═══════════════════════════════════════════════════════════════════════════

// Open commitment: ordered value not yet billed, on orders that are not closed.
// This is the enterprise "open purchase order" figure — what D&R has promised to
// pay and has not been billed for.
//
// opts.branchId is ACCEPTED AND IGNORED. Purchasing spans branches by design:
// Jennifer buys from Houston for requests that originate at Dallas, so a
// branch-scoped commitment figure would describe neither branch's obligations.
export function committedSpend(requests, opts = {}) {
  const rows = requests || [];
  if (!rows.length) return empty(EMPTY.NO_ORDERS);

  const live = rows.filter(r => r.status === 'ordered' || r.status === 'received');
  if (!live.length) return empty(EMPTY.NO_OPEN);

  let total = 0;
  let orderCount = 0;
  live.forEach(r => {
    const lineSum = itemsOf(r).reduce((s, it) => s + openCommitmentForLine(it), 0);
    if (lineSum > 0) { total += lineSum; orderCount += 1; }
  });

  if (orderCount === 0) return empty(EMPTY.NO_OPEN);
  return ok(total, { orderCount });
}

// Invoiced spend for the period, net of credits. The sign comes from
// document_type in exactly one place — netInvoiced() in vendorCredit.js — and is
// never recomputed here. Amounts are stored positive on both document types.
//
// opts.branchId accepted and ignored, same reason as committedSpend.
export function invoicedSpend(requests, opts = {}) {
  const docs = docsInPeriod(requests || [], opts.from, opts.to);
  if (!docs.length) return empty(EMPTY.NO_INVOICES);

  const invoiceCount = docs.filter(isInvoiceDoc).length;
  const creditCount = docs.filter(isCredit).length;

  return ok(netInvoiced(docs), { invoiceCount, creditCount });
}

// Top vendors by net invoiced spend, with each vendor's share of the total.
// Returns a list rather than a scalar — the empty contract still applies.
export function topVendorsBySpend(requests, opts = {}) {
  const limit = opts.limit || 5;
  const rows = requests || [];
  const byVendor = new Map();

  rows.forEach(r => {
    const docs = docsOf(r).filter(d => inPeriod(d.invoice_date, opts.from, opts.to));
    if (!docs.length) return;
    const key = r.vendor_id == null ? 'none' : String(r.vendor_id);
    const name = r.inventory_vendors?.name || 'Unassigned vendor';
    const prev = byVendor.get(key) || { vendorId: r.vendor_id, name, amount: 0, orders: 0 };
    prev.amount += netInvoiced(docs);
    prev.orders += 1;
    byVendor.set(key, prev);
  });

  const list = Array.from(byVendor.values()).sort((a, b) => b.amount - a.amount);
  if (!list.length) return empty(EMPTY.NO_INVOICES);

  const grand = list.reduce((s, v) => s + v.amount, 0);
  // Share is only meaningful against a positive total. A period whose credits
  // exceed its invoices is real and rare; reporting a share of it would produce
  // negative percentages rather than an error.
  const withShare = list.map(v => ({
    ...v,
    share: grand > 0 ? (v.amount / grand) * 100 : null,
  }));

  return ok(withShare.slice(0, limit), { grand, vendorCount: list.length });
}

// Value received and not yet billed — the month-end accrual figure. The Invoicing
// page's "Awaiting invoice" tab answers which ORDERS need billing; this answers
// how many DOLLARS are exposed, which is the number an accountant asks for.
//
// Per the approved gate: lines with something received that carry no invoice line
// at all. A line that is partly invoiced is deliberately not counted here — that
// is a partial-billing question rather than an accrual one, and folding the two
// together would make this figure mean two things at once.
export function receivedNotInvoiced(requests, opts = {}) {
  const rows = requests || [];
  if (!rows.length) return empty(EMPTY.NO_ORDERS);

  let total = 0;
  const orders = [];

  rows.forEach(r => {
    let orderTotal = 0;
    itemsOf(r).forEach(it => {
      const received = num(it.qty_received);
      const hasInvoiceLine = (it.material_request_invoice_lines || []).length > 0;
      if (received > 0 && !hasInvoiceLine) orderTotal += num(it.ordered_price);
    });
    if (orderTotal > 0) {
      total += orderTotal;
      orders.push({
        requestId: r.id,
        requestNumber: r.request_number,
        vendorName: r.inventory_vendors?.name || null,
        receivedAt: r.received_at || null,
        amount: orderTotal,
      });
    }
  });

  if (total === 0) return empty(EMPTY.ALL_INVOICED, 0);
  orders.sort((a, b) => b.amount - a.amount);
  return ok(total, { orders, orderCount: orders.length });
}

// ═══════════════════════════════════════════════════════════════════════════
// CONTROL
// ═══════════════════════════════════════════════════════════════════════════

// First-pass match rate — #302's calculation half.
//
// THE RULE ITSELF IS NOT REIMPLEMENTED HERE. firstPassRate() in
// invoiceDiscrepancy.js is the single definition: an invoice resolved as
// flagged-in-error leaves the calculation entirely, one resolved as
// price-accepted stays counted against it forever. Two things now read that
// number and they must not disagree about what it means, which is exactly why
// that file exists. This wrapper only adds the period filter and the empty
// contract that every other KPI here honours.
//
// Credits are excluded from the denominator: a credit memo is not an invoice
// that did or did not match an order, so counting one would move a process-health
// number for a reason unrelated to process health.
export function firstPassMatchRate(requests, opts = {}) {
  const invoices = docsInPeriod(requests || [], opts.from, opts.to).filter(isInvoiceDoc);
  if (!invoices.length) return empty(EMPTY.NO_INVOICES);

  const value = firstPassRate(invoices);
  // firstPassRate returns null when every invoice was a false flag and therefore
  // left the calculation. That is genuinely "nothing to measure", not zero.
  if (value == null) return empty(EMPTY.NO_INVOICES);

  const counted = invoices.filter(inv =>
    !(inv.discrepancy_resolved && inv.discrepancy_resolution === 'flagged_in_error')
  );
  const matched = counted.filter(inv => !inv.discrepancy_flagged).length;

  return ok(value, { matched, counted: counted.length });
}

// Money currently sitting in unresolved price discrepancies.
export function openDiscrepancyExposure(requests, opts = {}) {
  const invoices = docsInPeriod(requests || [], opts.from, opts.to).filter(isInvoiceDoc);
  const open = invoices.filter(inv => inv.discrepancy_flagged && !inv.discrepancy_resolved);

  if (!open.length) return empty(EMPTY.NOTHING_FLAGGED, 0);

  const total = open.reduce((s, inv) => s + Math.abs(num(inv.discrepancy_amount)), 0);
  return ok(total, { invoiceCount: open.length });
}

// Off-catalog spend as a share of ordered value — the maverick-spend measure.
// Benchmarked: under 5% is best-in-class, 15-20% is the industry average.
export function offCatalogRate(requests, opts = {}) {
  const rows = requests || [];
  let offTotal = 0;
  let grand = 0;
  let lineCount = 0;

  rows.forEach(r => {
    itemsOf(r).forEach(it => {
      const value = num(it.ordered_price);
      if (value <= 0) return;
      lineCount += 1;
      grand += value;
      if (it.is_off_catalog) offTotal += value;
    });
  });

  // An empty denominator is not zero percent. This is the distinction the whole
  // empty-result contract exists for.
  if (lineCount === 0 || grand <= 0) return empty(EMPTY.NO_LINES);

  return ok((offTotal / grand) * 100, { offTotal, grand, lineCount });
}

// Share of received lines that were closed short of what was ordered — the
// nearest honest thing Stede has to a fill rate. The dominant close reason is
// returned with it, because the rate on its own does not say whether the problem
// is one vendor or the whole process.
export function shortClosedRate(requests, opts = {}) {
  const rows = requests || [];
  let received = 0;
  let shortClosed = 0;
  const reasons = new Map();

  rows.forEach(r => {
    itemsOf(r).forEach(it => {
      const wasReceived = num(it.qty_received) > 0 || it.receipt_closed;
      if (!wasReceived) return;
      received += 1;
      if (it.receipt_closed && num(it.qty_received) < num(it.qty_requested)) {
        shortClosed += 1;
        const reason = it.receipt_close_reason || 'No reason recorded';
        reasons.set(reason, (reasons.get(reason) || 0) + 1);
      }
    });
  });

  if (received === 0) return empty(EMPTY.NO_RECEIPTS);

  const topReason = Array.from(reasons.entries()).sort((a, b) => b[1] - a[1])[0] || null;
  return ok((shortClosed / received) * 100, {
    shortClosed,
    received,
    topReason: topReason ? topReason[0] : null,
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// CYCLE TIME
// ═══════════════════════════════════════════════════════════════════════════

// Mean days from the request being raised to it being ordered.
// Benchmarked: under 5 business days for non-routine purchases.
export function requisitionToOrderDays(requests, opts = {}) {
  const spans = (requests || [])
    .map(r => days(r.created_at, r.ordered_at))
    .filter(d => d != null && d >= 0);

  if (!spans.length) return empty(EMPTY.NO_ORDERS);
  return ok(mean(spans), { sampleSize: spans.length });
}

// Mean days from order placed to order received, per vendor. A vendor with one
// receipt is reported with its sample size rather than hidden — an executive can
// discount n=1 themselves, but only if they are told.
export function orderToReceiptByVendor(requests, opts = {}) {
  const byVendor = new Map();

  (requests || []).forEach(r => {
    const span = days(r.ordered_at, r.received_at);
    if (span == null || span < 0) return;
    const key = r.vendor_id == null ? 'none' : String(r.vendor_id);
    const name = r.inventory_vendors?.name || 'Unassigned vendor';
    const prev = byVendor.get(key) || { vendorId: r.vendor_id, name, spans: [] };
    prev.spans.push(span);
    byVendor.set(key, prev);
  });

  const list = Array.from(byVendor.values())
    .map(v => ({ vendorId: v.vendorId, name: v.name, days: mean(v.spans), sampleSize: v.spans.length }))
    .sort((a, b) => a.days - b.days);

  if (!list.length) return empty(EMPTY.NO_RECEIPTS);
  return ok(list, { vendorCount: list.length });
}

// THIS IS NOT SUPPLIER ON-TIME DELIVERY AND MUST NEVER BE LABELLED AS IT.
//
// True OTD measures against a date the VENDOR promised. Stede holds needed_by
// (what the requester asked for) and tracking_eta (what the carrier predicts).
// Neither is a promise from the vendor, so this measures whether the requester
// got what they needed when they needed it — a real and useful figure, and a
// different one. A promised_date column is the Phase 2 prerequisite for real OTD.
//
// Suppressed below MIN_NEED_BY_SAMPLE because needed_by is optional: on a handful
// of lines this reports the habits of whoever happens to fill the field in.
export const MIN_NEED_BY_SAMPLE = 5;

export function deliveredByNeedBy(requests, opts = {}) {
  const scoped = scopeToBranch(requests || [], opts.branchId);
  let onTime = 0;
  let total = 0;

  scoped.forEach(r => {
    if (!r.received_at) return;
    itemsOf(r).forEach(it => {
      if (!it.needed_by) return;
      total += 1;
      if (String(r.received_at).slice(0, 10) <= String(it.needed_by).slice(0, 10)) onTime += 1;
    });
  });

  if (total < MIN_NEED_BY_SAMPLE) {
    return empty(EMPTY.NO_NEED_BY, MIN_NEED_BY_SAMPLE - total);
  }
  return ok((onTime / total) * 100, { onTime, total });
}

// ═══════════════════════════════════════════════════════════════════════════
// INVENTORY
// ═══════════════════════════════════════════════════════════════════════════

// Takes inventory_stock rows, not requests — a different source, so a different
// signature. This is the one KPI that is genuinely populated on September 1,
// because stock exists before any order is placed.
//
// Branch scoping IS applied here. Inventory is held at a branch and a branch
// manager's low-stock count is a real question with a real answer, unlike a
// branch-scoped purchasing figure.
//
// #511, Session 79.22b.6.1f -- THE COUNT USES THE SHARED RULE, needsAttention().
// It was `belowReorderPoint`, and it filtered to rows WITH a reorder point before
// comparing -- so an empty shelf nobody had set a threshold for was counted as
// fine. That is the exact defect #357 ended on Stock Home, the Use screen and
// the Catalog, surviving here: on production 44 of 45 stock rows carry no
// reorder point, so the tile could see almost nothing. LOW needs a reorder
// point; OUT does not. The set is now exactly what Stock Home lists, and the
// tile is named for it.
//
// #803, Session 79.22b.6.5.2 -- AND ONLY SWITCHED-ON ITEMS, which is the other
// half of "what Stock Home lists". Stock Home leaves out a row whose item has
// been switched off (InventoryStockHome.js: no item, or `is_active` not true);
// this counted every stock row, so the tile read 57 on the test database where
// Stock Home's set is 38 -- a manager told about empty shelves for things
// nobody stocks any more. A row that carries its item (`inventory_items`, as
// Insights reads it) is judged by Stock Home's rule; a caller that did not read
// the item cannot be judged and is counted as before.
export function lowOrOutOfStock(stockRows, opts = {}) {
  let rows = (stockRows || []).filter(s => !('inventory_items' in s) || s.inventory_items?.is_active === true);
  if (opts.branchId != null) {
    rows = rows.filter(s => String(s.branch_id) === String(opts.branchId));
  }
  if (!rows.length) return empty(EMPTY.NO_STOCK);

  const flagged = rows.filter(s => needsAttention(num(s.qty_on_hand), s.reorder_point == null ? null : num(s.reorder_point)));
  // qty_on_hand can legitimately go negative -- the app warns and never blocks --
  // and Out covers it, so the out figure is every row at or below zero.
  const out = rows.filter(s => num(s.qty_on_hand) <= 0);

  return ok(flagged.length, {
    out: out.length,
    tracked: rows.filter(s => s.reorder_point != null).length,
    total: rows.length,
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// BENCHMARKS
// ═══════════════════════════════════════════════════════════════════════════

// Published industry figures, kept beside the definitions they qualify and
// labelled with their source so nobody later mistakes them for D&R targets.
// Rendered as a tile's subtitle, never as its value — see the gate, callout D.
export const BENCHMARKS = {
  offCatalogRate: {
    label: 'industry avg 15-20%',
    best: 5,
    average: [15, 20],
    source: 'Maverick-spend benchmarks, procurement KPI literature',
  },
  requisitionToOrderDays: {
    label: 'target under 5 days',
    best: 1,
    average: [5, 5],
    source: 'PO cycle time: under 24h routine, under 5 business days complex',
  },
  firstPassMatchRate: {
    label: 'top performers 90%+',
    best: 95,
    average: [70, 80],
    source: 'Invoice first-pass match rate, AP benchmark literature',
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// WHAT IS DELIBERATELY NOT HERE
// ═══════════════════════════════════════════════════════════════════════════
//
// AGING OF HELD OBLIGATIONS. computeAging() and AGING_BUCKETS already exist in
// InvoicingPage.js, shipped by #300 at Session 74z3. Writing a second bucket
// definition here would create two ways to compute one money figure, which is
// precisely what invoiceDiscrepancy.js's own header says that file exists to
// prevent. The correct move is to lift computeAging into a shared module and
// import it in both places — an extraction that touches a money-path file and
// belongs with the Sep 5-6 page build, not bolted onto a session that already
// carries this layer. The dashboard tile drills into the strip that exists.
//
// DAYS PAYABLE OUTSTANDING and ON-TIME PAYMENT RATE. Not blocked — decided.
// Colby ruled at 74z2 that QuickBooks always owns billing and payment, recorded
// inside #300. Stede holds no payment date by design.
//
// PO COVERAGE RATE. Every invoice in Stede is entered against a request, so it
// reads 100% by construction. An enterprise KPI that can only ever read 100%
// teaches an executive that the screen is decorative.
//
// COST SAVINGS and SPEND UNDER MANAGEMENT. The first needs a price baseline that
// does not exist on day one; the second needs total company spend including
// everything that never touches Stede. Both would be guesses wearing percentages.
